import React, { useState, useContext, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faUser, faCog, faSignOutAlt, faCrown, faMapMarkerAlt, faHeart, faCalendar, faQuestionCircle, faUmbrellaBeach, faUtensils, faHotel, faLandmark, faMountain, faTree } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../contexts/AuthContext';
import Weather from './Weather';
import LanguageSelector from './LanguageSelector';
import ExchangeRate from './ExchangeRate';
import AuthModal from './AuthModal';
import ConfirmationModal from './ConfirmationModal';
import { ReactComponent as QQIcon } from '../resources/QQ-Icon.svg';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import config from '../config/config';

const NavbarContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  height: 88px;
  width: 100%;
  gap: 24px;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px 8px;
    gap: 12px;
  }
`;

const LeftSection = styled.div`
  flex: 0 0 auto;
  margin-left: 16px;
  background: rgba(255, 255, 255, 0.3);
  box-shadow: inset 1px 1px 8px rgba(255, 255, 255, 0.64);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 32px;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  padding: 0 24px;
  height: 48px;
  min-width: fit-content;

  @media (max-width: 1201px) {
    padding: 0 12px;
    min-width: fit-content;
    margin-left: 8px;
  }
`;

const RightSection = styled.div`
  flex: 0 0 auto;
  background: #14464F;
  border-radius: 46px;
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  margin-right: 16px;
  height: 48px;
  min-width: 200px;

  @media (max-width: 1140px) {
    padding: 8px;
    width: 48px;
    min-width: auto;
    justify-content: center;
    align-items: center;
    margin-right: 8px;
  }
`;

const LogoIcon = styled(QQIcon)`
  width: 48px;
  height: 32px;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const LogoText = styled.span`
  @media (max-width: 1201px) {
    display: none;
  }
`;

const Logo = styled(Link)`
  font-family: 'Josefin Sans';
  font-weight: 700;
  font-size: 24px;
  color: #14464F;
  text-decoration: none;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 0;
`;

const WidgetsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  height: 100%;

  @media (max-width: 1140px) {
    display: none;
  }
`;

const RightAlignedItems = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  height: 100%;

  @media (max-width: 1140px) {
    .language-selector {
      display: none;
    }
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 32px;
  margin-left: 8px;

  @media (max-width: 1140px) {
    margin-left: 0;
    width: 24px;
    height: 24px;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: 80px;
  right: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 8px;
  z-index: 1500;
  min-width: 200px;
`;

const MenuItem = styled(({ isButton, ...props }) => 
  isButton ? <button {...props} /> : <Link {...props} />
)`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: #333;
  text-decoration: none;
  border-radius: 8px;
  gap: 12px;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  font-size: inherit;
  text-align: left;

  &:hover {
    background: #f5f5f5;
  }

  svg {
    width: 16px;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #eee;
  margin: 8px 0;
`;

const CenterSection = styled.div`
  flex: 1 1 auto;
  background: rgba(255, 255, 255, 0.3);
  box-shadow: inset 1px 1px 8px rgba(255, 255, 255, 0.64);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 32px;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  overflow-x: auto;
  min-width: 200px;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  height: 48px;
  padding: 0 8px;
  
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    min-width: 150px;
    padding: 0 4px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 auto;
  width: fit-content;
  min-width: min-content;

  ${CenterSection}:hover & {
    margin: ${props => props.isOverflowing ? '0' : '0 auto'};
  }
`;

const FilterItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  color: #14464F;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 20px;
  background: ${props => props.active ? 'white' : 'transparent'};
  font-size: 13px;
  white-space: nowrap;
  flex-shrink: 0;
  height: 36px;

  &:first-child {
    margin-left: 4px;
  }

  &:last-child {
    margin-right: 4px;
  }

  &:hover {
    color: #14464F;
    background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  }

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
    filter: ${props => props.active ? 'brightness(0) saturate(100%) invert(56%) sepia(93%) saturate(385%) hue-rotate(334deg) brightness(97%) contrast(94%)' : 'brightness(0) saturate(100%) invert(24%) sepia(29%) saturate(1014%) hue-rotate(154deg) brightness(94%) contrast(89%)'};
  }

  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 11px;
    gap: 4px;
    height: 32px;
    
    img {
      width: 14px;
      height: 14px;
    }
  }
`;

const LanguageContainer = styled.div`
  padding-top: 5px;
  height: 100%;
  display: flex;
  align-items: center;
`;

const MenuWidgets = styled.div`
  display: none;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;

  @media (max-width: 1140px) {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    width: 100%;
    padding: 12px 8px;
    gap: 8px;

    .weather-widget, 
    .exchange-widget,
    .language-widget {
      color: #14464F;
      flex: 0 1 auto;
      display: flex;
      justify-content: center;
      align-items: center;
      
      svg, 
      button,
      select {
        color: #14464F;
      }
    }

    .exchange-widget {
      margin-left: -30px;
    }
  }
`;

const Navbar = ({ onFilterChange, activeFilters = [] }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [activePopup, setActivePopup] = useState(null);
  const filterContainerRef = React.useRef(null);
  const centerSectionRef = React.useRef(null);
  const { isAuthenticated, logout } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [amenities, setAmenities] = useState([]);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const response = await fetch(`${config.API_URL}/general/amenities`);
        const data = await response.json();
        setAmenities(data);
      } catch (error) {
        console.error('Error fetching amenities:', error);
      }
    };

    fetchAmenities();
  }, []);

  useEffect(() => {
    const checkOverflow = () => {
      if (filterContainerRef.current && centerSectionRef.current) {
        const isContentOverflowing = filterContainerRef.current.scrollWidth > centerSectionRef.current.clientWidth;
        setIsOverflowing(isContentOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [amenities]);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
    setIsMenuOpen(false);
  };

  const handleLogoutConfirm = () => {
    logout();
    history.push('/');
    setShowLogoutConfirm(false);
  };

  const handleLoginClick = () => {
    setShowAuthModal(true);
    setIsMenuOpen(false);
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
  };

  const handleFilterClick = (value) => {
    if (onFilterChange) {
      if (activeFilters.includes(value)) {
        onFilterChange(activeFilters.filter(f => f !== value));
      } else {
        onFilterChange([...activeFilters, value]);
      }
    }
  };

  const handlePopupToggle = (popupName) => {
    if (popupName === 'menu') {
      setIsMenuOpen(!isMenuOpen);
    } else {
      setIsMenuOpen(false);
    }
    setActivePopup(activePopup === popupName ? null : popupName);
    if (popupName !== 'auth') setShowAuthModal(false);
  };

  return (
    <NavbarContainer>
      <LeftSection>
        <Logo to="/">
          <LogoIcon />
          <LogoText>Quisqueya Quest</LogoText>
        </Logo>
      </LeftSection>

      {isHomePage && (
        <CenterSection ref={centerSectionRef}>
          <FilterContainer ref={filterContainerRef} isOverflowing={isOverflowing}>
            {amenities.map(amenity => (
              <FilterItem
                key={amenity.id}
                active={activeFilters.includes(amenity.id)}
                onClick={() => handleFilterClick(amenity.id)}
              >
                <img src={amenity.icon} alt={language === 'es' ? amenity.name_es : amenity.name} />
                {language === 'es' ? amenity.name_es : amenity.name}
              </FilterItem>
            ))}
          </FilterContainer>
        </CenterSection>
      )}

      <RightSection>
        <WidgetsContainer>
          <Weather />
          <ExchangeRate onPopupToggle={() => handlePopupToggle('exchange')} isOpen={activePopup === 'exchange'} />
        </WidgetsContainer>
        <RightAlignedItems>
          <LanguageContainer className="language-selector">
            <LanguageSelector onPopupToggle={() => handlePopupToggle('language')} isOpen={activePopup === 'language'} />
          </LanguageContainer>
          <MenuButton onClick={() => handlePopupToggle('menu')}>
            <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
          </MenuButton>
        </RightAlignedItems>
      </RightSection>

      {isMenuOpen && (
        <Menu>
          <MenuWidgets>
            <div className="weather-widget">
              <Weather />
            </div>
            <div className="exchange-widget">
              <ExchangeRate />
            </div>
            <div className="language-widget">
              <LanguageSelector />
            </div>
          </MenuWidgets>
          
          {isAuthenticated ? (
            <>
              <MenuItem to="/profile">
                <FontAwesomeIcon icon={faUser} />
                {translations[language].navbar.profile}
              </MenuItem>
              <MenuItem to="/my-trips">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                {translations[language].navbar.myTrips}
              </MenuItem>
              <MenuItem to="/favorites">
                <FontAwesomeIcon icon={faHeart} />
                {translations[language].navbar.favorites}
              </MenuItem>
              <MenuItem to="/bookings">
                <FontAwesomeIcon icon={faCalendar} />
                {translations[language].navbar.bookings}
              </MenuItem>
              <MenuItem to="/faq">
                <FontAwesomeIcon icon={faQuestionCircle} />
                {translations[language].navbar.faq}
              </MenuItem>
              <MenuDivider />
              <MenuItem isButton onClick={handleLogoutClick}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                {translations[language].navbar.logout}
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem to="/faq">
                <FontAwesomeIcon icon={faQuestionCircle} />
                {translations[language].navbar.faq}
              </MenuItem>
              <MenuDivider />
              <MenuItem isButton onClick={handleLoginClick}>
                <FontAwesomeIcon icon={faUser} />
                {translations[language].navbar.login}
              </MenuItem>
            </>
          )}
        </Menu>
      )}

      {showAuthModal && (
        <AuthModal 
          onClose={() => setShowAuthModal(false)} 
          onAuthSuccess={handleAuthSuccess}
        />
      )}

      {showLogoutConfirm && (
        <ConfirmationModal
          isOpen={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
          onConfirm={handleLogoutConfirm}
          title={translations[language].navbar.confirmLogout.title}
          message={translations[language].navbar.confirmLogout.message}
        />
      )}
    </NavbarContainer>
  );
};

export default Navbar;
